// Add the todo in to the database
import { ContentfulPaths, LocationStatePayload } from "../redux/app/types";
import { createClient } from "contentful";
import safeStringify from "fast-safe-stringify";
import { Card, Page, TeamMember, AllPages } from "../types";
import { validateInputArray } from "./validation";
import { v4 as uuidv4 } from "uuid";
import Cookie from "js-cookie";
import { FbEvent } from '../../utils/fbq';
declare global {
  interface Window {
    analytics: any;
    ga: any;
  }
}

const getClient = (isPreview) => {
  return createClient({
    space: process.env.CONTENTFUL_SPACE_ID,
    accessToken: isPreview
        ? process.env.CONTENTFUL_PREVIEW_ACCESS_TOKEN
        : process.env.CONTENTFUL_ACCESS_TOKEN,
    host: isPreview ? "preview.contentful.com" : "cdn.contentful.com",
  });
}

export const reduceToIDs = (acc: number[], val: { id: number }) => {
  acc.push(val.id);
  return acc;
};

export const fetchContentfulGlobals = async (
  isPreview: boolean
): Promise<AllPages> => {
  try {
    const client = getClient(isPreview)
    const { items } = (await client.getEntries({
      include: 3,
      content_type: "allPages",
    })) as any;

    const safeItems = JSON.parse(safeStringify(items)) as any;
    const item = safeItems[0];
    return {
      id: item.sys.id,
      title: item.fields.websiteTitle,
      defaultPhoneNumber: item.fields.defaultPhoneNumber,
      googleTagManagerId: item.fields.googleTagManagerId || "",
      navigation: item.fields.navigation,
      operatingTimes: item.fields.operatingTimes
    };
  } catch (error) {
    console.error(error);
    return error;
  }
};

export const fetchContentfulEntries = async (
  isPreview = false
): Promise<ContentfulPaths[]> => {
  try {
    const client = getClient(isPreview)
    const { items } = await client.getEntries({
      content_type: "page",
    });
    const safeItems = JSON.parse(safeStringify(items)).filter(
      (item) =>
        item.fields.fullSlug &&
        item.fields.fullSlug !== "/" &&
        item.fields.fullSlug !== "blog"
    ) as any;
    // Do something async here
    return (safeItems as any).map((item) => {
      return {
        params: {
          slug: item.fields.fullSlug.split("/"),
        },
        //   id: item.sys.id,
      };
    });
  } catch (error) {
    console.error(error);
    return error;
  }
};

export const fetchContentfulTeamMembers = async (
  isPreview
): Promise<TeamMember[]> => {
  try {
    const client = getClient(isPreview)
    const { items } = await client.getEntries({
      content_type: "teamMember",
    });
    if (!items) return [];
    const safeItems = JSON.parse(safeStringify(items));
    const teamMembers: TeamMember[] = safeItems.map((item) => {
      return {
        fullName: item.fields.fullName,
        portraitImage: item.fields.portraitImage || null,
        content: item.fields.content || null,
        state: item.fields.state,
        membershipsAssociations: item.fields.membershipsAssociations || null,
        positions: item.fields.positions,
      };
    });
    return teamMembers;
  } catch (error) {
    console.error(error);
    return error;
  }
};
export type PostDateFilter = {
  start: string;
  end: string;
};

export type PostFilters = {
  length: number;
  category?: string;
  date?: PostDateFilter;
};
export const fetchContentfulPosts = async (
  isPreview: boolean,
  filters: PostFilters
): Promise<Card[]> => {
  try {
    const client = getClient(isPreview)
    const options = {
      content_type: "page",
      "fields.template": "Post",
      include: filters.length,
    };
    if (filters.category) options["fields.category[in]"] = filters.category;
    if (filters.date && filters.date.start && filters.date.end) {
      options["sys.createdAt[gte]"] = filters.date.start;
      options["sys.createdAt[lte]"] = filters.date.end;
    }
    const { items } = await client.getEntries(options);
    if (!items) return [];
    const safeItems = JSON.parse(safeStringify(items));

    // Do something async here
    const cards: Card[] = safeItems.map((item) => {
      return {
        title: item.fields.pageTitle,
        slug: item.fields.fullSlug,
        containsPipa: item.fields.containsPipa || false,
        hero:
          item.fields.hero &&
          item.fields.hero[0] &&
          item.fields.hero[0].fields.blocks &&
          item.fields.hero[0].fields.blocks[0].fields.featureImage &&
          item.fields.hero[0].fields.blocks[0].fields.featureImage.fields,
        category: item.fields.category || [],
      };
    });
    return cards;
  } catch (error) {
    console.error(error);
    return error;
  }
};

export const fetchContentfulEntry = async (
  slug: string,
  isPreview: boolean
): Promise<Page> => {
  try {
    const client = getClient(isPreview)

    const { items } = (await client.getEntries({
      include: 3,
      "fields.fullSlug": slug,
      content_type: "page",
    })) as any;

    const safeItems = JSON.parse(safeStringify(items)) as any;
    const item = safeItems[0];
    return {
      id: item.sys.id,
      title: item.fields.pageTitle,
      navigationTitle: item.fields.navigationTitle,
      seoPageTitle: item.fields.seoPageTitle || item.fields.navigationTitle,
      seoPageDescription: item.fields.seoPageDescription || "",
      sections: item.fields.sections,
      hero: item.fields.hero || [],
      containsPipa: item.fields.containsPipa || false,
      template: item.fields.template,
      category: item.fields.category || [],
    };
  } catch (error) {
    console.error(error);
    return error;
  }
};

export const fetchGeolocation = async (): Promise<any> => {
  try {
    const response = (await fetch(
      "https://services-stage.shine.com.au/v2/geolocate"
    )) as any;
    const data = await response.json();
    return data as LocationStatePayload;
  } catch (error) {
    console.error(error);
    return error;
  }
};

export const handleSubmit = async (form, setForm, recaptchaRef) => {
  if (typeof window === `undefined`) {
    return;
  }
  const recaptchaToken = await recaptchaRef.current.getValue();
  if (!recaptchaToken) return { error: "Error when fetching recaptcha token." };
  const { fields } = form;
  const validate = validateInputArray(fields);
  if (validate.find((f) => f.error !== false)) return;
  setForm({ ...form, fields: validate });

  const sendArray = fields.map((field) => {
    return { label: field.label, value: field.value };
  });
  const sendObject = { ...sendArray };
  setForm({ ...form, submitting: true });
  const googleId = getGoogleID();
  const segmentId = getSegmentID();
  const campaign = {
    name: Cookie.get(`campaign`) || ``,
    source: Cookie.get(`source`) || ``,
    medium: Cookie.get(`medium`) || ``,
  };

  FbEvent('CompleteRegistration');


  const submitObject = {
    formID: `ESLEnquiryForm`,
    formName: `Enquire Now`,
    emailTo: [window.location.hostname === `www.emergencyserviceslawyers.com.au` ? `contact@emergencyserviceslawyers.com.au` : `ecommerce@shine.com.au`],
    isProd: true,
    site: "Emergency Service Lawyers",
    fields: {
      ...sendObject,
      googleID: {
        label: `GoogleID`,
        value: googleId,
        isFile: false,
      },
      submittedTime: {
        label: `SubmittedTime`,
        value: new Date().toISOString(),
        isFile: false,
      },
      segmentID: {
        label: `SegmentID`,
        value: segmentId,
        isFile: false,
      },
      submitURL: {
        label: `submitURL`,
        value: window.location.href,
        isFile: false,
      },
      workType: {
        label: `Work Type`,
        value: "#aoep3",
        isFile: false,
      },
    },
    analytics: {
      anonymousId: segmentId,
      userId: segmentId,
      event: `ESL Form Submitted`,
      properties: { category: `Form`, label: `Enquire Now` },
      context: {
        page: {
          url: window.location.href,
          title: document.title,
          referrer: document.referrer,
          path: window.location.pathname,
        },
        campaign,
        "Google Analytics": { clientId: googleId },
      },
    },
  };
  try {
    const rawResponse = await fetch(
      `https://services.shine.com.au/api/form/submit/FormBuilder?code=dVhaa5xfOlsQOJ8wwBXp6qsWhV6gMp9rHCn9tB8Kg7UlT3iGVUMNWg==&timestamp=${new Date().getTime()}`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submitObject),
      }
    );
    await rawResponse.json();
    setForm({ ...form, submitting: false, submitted: true, error: false });
  } catch (error) {
    setForm({
      ...form,
      submitting: false,
      submitted: false,
      error:
        "Something went wrong while submitting. Please try again later or call us on 1800 954 754",
    });
    console.error(error);
  }
};

export const getSegmentID = () =>
  typeof window !== `undefined` &&
  typeof window.analytics === `object` &&
  typeof window.analytics.user === `function`
    ? window.analytics.user().anonymousId()
    : uuidv4();

export const getGoogleID = () => {
  let googleID = null;
  if (typeof window.ga === `function` && typeof window.ga.getAll === `function`)
    googleID = window.ga.getAll()[0].get(`clientId`);
  else if (Cookie.get(`_ga`)) {
    let str = Cookie.get(`_ga`);
    str = str.slice(6);
    googleID = str;
  }
  return googleID;
};
